import React from 'react';
import {
  KoobSegmentedControl,
  KoobSegmentedControlItem,
} from './KoobSegmentedControl';

type KoobInputSegmentedOption = {
  value: unknown;
  label: React.ReactNode;
  disabled?: boolean;
};

type KoobInputSegmentedProps = {
  options: KoobInputSegmentedOption[];
  value?: unknown;
  onChange?: (value: unknown) => void;
  label?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md';
};

// Form-input flavor of KoobSegmentedControl: accepts arbitrary option values
// (booleans, null, numbers…) and bridges them to Radix's string-only values
// via the option index.
const KoobInputSegmented = React.forwardRef<
  HTMLDivElement,
  KoobInputSegmentedProps
>(
  (
    { options, value, onChange, label, error, required, disabled, size },
    ref,
  ) => {
    const selectedIndex = options.findIndex((option) => option.value === value);

    return (
      // min-w-0 so the control shrinks (and truncates) inside flex rows
      // instead of pushing its container wider.
      <div className="min-w-0">
        {label && (
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}
        <KoobSegmentedControl
          ref={ref}
          size={size}
          disabled={disabled}
          value={selectedIndex >= 0 ? String(selectedIndex) : ''}
          onValueChange={(index) => onChange?.(options[Number(index)]?.value)}
        >
          {options.map((option, index) => (
            <KoobSegmentedControlItem
              key={index}
              value={String(index)}
              disabled={option.disabled}
              data-testid={`${String(option.value)}-option`}
            >
              {option.label}
            </KoobSegmentedControlItem>
          ))}
        </KoobSegmentedControl>
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
      </div>
    );
  },
);

KoobInputSegmented.displayName = 'KoobInputSegmented';

export default KoobInputSegmented;
